import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Row,
  Col,
  Typography,
  Avatar,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { usersAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';

const { Title } = Typography;
const { Option } = Select;

interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [form] = Form.useForm();
  const { user: currentUser } = useAuthStore();

  const isAdmin = currentUser?.role === 'admin';

  // Get user list
  const fetchUsers = async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const response = await usersAPI.getUsers();
      let filteredUsers = response.data;
      
      // Client-side filtering
      if (searchText) {
        filteredUsers = filteredUsers.filter((user: User) => 
          user.username.toLowerCase().includes(searchText.toLowerCase()) ||
          user.email.toLowerCase().includes(searchText.toLowerCase()) ||
          user.full_name.toLowerCase().includes(searchText.toLowerCase())
        );
      }
      
      if (roleFilter) {
        filteredUsers = filteredUsers.filter((user: User) => user.role === roleFilter);
      }
      
      setUsers(filteredUsers);
    } catch (error) {
      message.error('Failed to fetch user list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchText, roleFilter]);

  // Handle user creation/update
  const handleSubmit = async (values: any) => {
    try {
      if (editingUser) {
        await usersAPI.updateUser(editingUser.id, values);
        message.success('User updated successfully');
      } else {
        await usersAPI.createUser(values);
        message.success('User created successfully');
      }
      setModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  // Handle user deletion
  const handleDelete = async (id: number) => {
    try {
      await usersAPI.deleteUser(id);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      message.error('Delete failed');
    }
  };

  // Open edit modal
  const openEditModal = (user?: User) => {
    setEditingUser(user || null);
    if (user) {
      form.setFieldsValue({
        username: user.username,
        email: user.email,
        full_name: user.full_name,
        role: user.role,
        is_active: user.is_active,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ is_active: true, role: 'sales' });
    }
    setModalVisible(true);
  };

  // Role color mapping
  const getRoleColor = (role: string) => {
    const colorMap: Record<string, string> = {
      'admin': 'red',
      'ceo': 'purple',
      'sales': 'blue',
      'analyst': 'green',
    };
    return colorMap[role] || 'default';
  };

  // Role text mapping
  const getRoleText = (role: string) => {
    const textMap: Record<string, string> = {
      'admin': 'Administrator',
      'ceo': 'CEO',
      'sales': 'Sales',
      'analyst': 'Analyst',
    };
    return textMap[role] || role;
  };

  // Table column definition
  const columns: ColumnsType<User> = [
    {
      title: 'User',
      key: 'user_info',
      width: 200,
      render: (_, record) => (
        <Space>
          <Avatar 
            size={40} 
            icon={<UserOutlined />}
            style={{ backgroundColor: getRoleColor(record.role) }}
          >
            {record.full_name.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.full_name}</div>
            <div style={{ fontSize: 12, color: '#86909C' }}>@{record.username}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (role: string) => (
        <Tag color={getRoleColor(role)}>
          {getRoleText(role)}
        </Tag>
      ),
      filters: [
        { text: 'Administrator', value: 'admin' },
        { text: 'CEO', value: 'ceo' },
        { text: 'Sales', value: 'sales' },
        { text: 'Analyst', value: 'analyst' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      width: 120,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'Never logged in',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                Modal.info({
                  title: 'User Details',
                  width: 500,
                  content: (
                    <div>
                      <p><strong>Username:</strong> {record.username}</p>
                      <p><strong>Full Name:</strong> {record.full_name}</p>
                      <p><strong>Email:</strong> {record.email}</p>
                      <p><strong>Role:</strong> {getRoleText(record.role)}</p>
                      <p><strong>Status:</strong> {record.is_active ? 'Active' : 'Inactive'}</p>
                      <p><strong>Created At:</strong> {new Date(record.created_at).toLocaleString()}</p>
                      <p><strong>Last Login:</strong> {record.last_login ? new Date(record.last_login).toLocaleString() : 'Never logged in'}</p>
                    </div>
                  ),
                });
              }}
            />
          </Tooltip>
          {isAdmin && record.id !== currentUser?.id && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => openEditModal(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Popconfirm
                  title="Are you sure you want to delete this user?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="OK"
                  cancelText="Cancel"
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  if (!isAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Title level={3}>Access Denied</Title>
        <p>Only administrators can access the user management page</p>
      </div>
    );
  }

  return (
    <div>
      {/* Page title */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>User Management</Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openEditModal()}
          >
            Add User
          </Button>
        </Col>
      </Row>

      {/* Filter area */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search username, email or name"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Select Role"
              value={roleFilter}
              onChange={setRoleFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="admin">Administrator</Option>
              <Option value="ceo">CEO</Option>
              <Option value="sales">Sales</Option>
              <Option value="analyst">Analyst</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchUsers}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* User table */}
      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            total: users.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* User edit modal */}
      <Modal
        title={editingUser ? 'Edit User' : 'Add User'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_active: true, role: 'sales' }}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please enter username' },
              { min: 3, message: 'Username must be at least 3 characters' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: 'Username can only contain letters, numbers and underscores' },
            ]}
          >
            <Input placeholder="Please enter username" disabled={!!editingUser} />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          >
            <Input placeholder="Please enter email" />
          </Form.Item>

          <Form.Item
            name="full_name"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter full name' }]}
          >
            <Input placeholder="Please enter full name" />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter password' },
                { min: 6, message: 'Password must be at least 6 characters' },
              ]}
            >
              <Input.Password placeholder="Please enter password" />
            </Form.Item>
          )}

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select placeholder="Please select role">
              <Option value="admin">Administrator</Option>
              <Option value="ceo">CEO</Option>
              <Option value="sales">Sales</Option>
              <Option value="analyst">Analyst</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Select>
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingUser(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersPage;